import { trackClientEvent } from 'analytics/analytics'
import { getCheckoutObservabilityState } from 'storage/checkout'

import {
  checkoutObservabilityContext,
  type CheckoutObservabilityContextData,
  type CheckoutObservabilityState,
} from './eventContexts/checkoutObservabilityContext'
import { checkoutSessionContext } from './eventContexts/checkoutSessionContext'

type CheckoutObservabilitySubject = 'attempt' | 'step' | 'module' | 'payment' | 'order'
type CheckoutObservabilityAction = 'entered' | 'exited_checkout' | 'restore_linked' | CheckoutObservabilityState

function isCheckoutObservabilityState(action: CheckoutObservabilityAction): action is CheckoutObservabilityState {
  return action !== 'entered' && action !== 'exited_checkout' && action !== 'restore_linked'
}

interface TrackCheckoutObservabilityEventParams extends Omit<CheckoutObservabilityContextData, 'state'> {
  subject: CheckoutObservabilitySubject
  action: CheckoutObservabilityAction
  type: 'interaction' | 'nonInteraction' | 'operational'
}

export function trackCheckoutObservabilityEvent({ subject, action, type, ...contextData }: TrackCheckoutObservabilityEventParams): boolean {
  const attempt = getCheckoutObservabilityState().activeAttempt
  if (!attempt) return false

  trackClientEvent({
    category: 'checkout',
    subject,
    action,
    type,
    context: [
      checkoutSessionContext({
        checkoutSessionId: attempt.checkoutAttemptId,
        checkoutLineageId: attempt.checkoutLineageId,
      }),
      ...(isCheckoutObservabilityState(action)
        ? [checkoutObservabilityContext({ state: action, ...contextData })]
        : []),
    ],
  })

  return true
}

export function trackCheckoutValidationFailure(reasonCode: 'page_validation_failed' | 'native_form_invalid'): boolean {
  return trackCheckoutObservabilityEvent({
    subject: 'step',
    action: 'validation_failed',
    type: 'nonInteraction',
    reasonCode,
    isBlocking: true,
    canContinue: false,
  })
}
